import React from 'react';
import { Box, Dialog, DialogTitle, DialogContent } from '@mui/material';
import { DrgView } from '../../../../../../services/ContractService/types/contractTypes';
import { useDrgForm } from './hooks';
import {
    DrgCodeTitleFields,
    WeightLosFields,
    GrouperSelect,
    RateCalculationDisplay,
    DialogActionButtons
} from './components';

interface AddEditDrgDialogProps {
    open: boolean;
    editingDrg: DrgView | null;
    feeScheduleId: string;
    onClose: () => void;
}

const AddEditDrgDialog: React.FC<AddEditDrgDialogProps> = ({
    open,
    editingDrg,
    feeScheduleId,
    onClose,
}) => {
    const {
        formData,
        formErrors,
        isSaving,
        isDeleting,
        isLoading,
        availableGroupers,
        calculatedRate,
        selectedFeeSchedule,
        handleFormChange,
        handleSave,
        handleDelete
    } = useDrgForm({
        editingDrg,
        feeScheduleId,
        open,
        onClose
    });

    const baseRate = selectedFeeSchedule?.groupers?.find(
        (g: { code: string; rate: number }) => g.code === formData.grouperCode
    )?.rate || 0;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {editingDrg ? 'Edit DRG' : 'Add DRG'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <DrgCodeTitleFields
                        code={formData.code}
                        title={formData.title}
                        codeError={formErrors.code}
                        titleError={formErrors.title}
                        onCodeChange={(value) => handleFormChange('code', value)}
                        onTitleChange={(value) => handleFormChange('title', value)}
                    />

                    <WeightLosFields
                        weight={formData.weight}
                        arithmeticMeanLOS={formData.arithmeticMeanLOS}
                        weightError={formErrors.weight}
                        losError={formErrors.arithmeticMeanLOS}
                        onWeightChange={(value) => handleFormChange('weight', value)}
                        onLosChange={(value) => handleFormChange('arithmeticMeanLOS', value)}
                    />

                    <GrouperSelect
                        grouperCode={formData.grouperCode}
                        availableGroupers={availableGroupers}
                        error={formErrors.grouperCode}
                        onChange={(value) => handleFormChange('grouperCode', value)}
                    />

                    <RateCalculationDisplay
                        grouperCode={formData.grouperCode}
                        weight={formData.weight}
                        baseRate={baseRate}
                        calculatedRate={calculatedRate}
                    />
                </Box>
            </DialogContent>

            <DialogActionButtons
                isEditing={!!editingDrg}
                isLoading={isLoading}
                isSaving={isSaving}
                isDeleting={isDeleting}
                onCancel={onClose}
                onDelete={handleDelete}
                onSave={handleSave}
            />
        </Dialog>
    );
};

export default AddEditDrgDialog;
